import { datadogRum } from '@datadog/browser-rum';
import type { ModalProps } from '@usealloy/component-library';
import {
  Box,
  Modal,
  Text,
  useSnackBar,
} from '@usealloy/component-library';
import React, { useEffect, useState, useContext } from 'react';
import { UserContext } from '~/dashboard/context';
import type { Document, Entity } from '~/entities/types';
import { useFeatureFlags } from '~/dashboard/hooks';
import { DocumentUploadPopover } from '~/documents/DocumentUploadPopover';

import { DocumentReviewer } from './DocumentReviewer';

// ============================================================
//     TYPES
// ============================================================
export type EntityForDisplay = Pick<Entity, 'entity_name' | 'entity_token'>;

export type EntitiesMap = {
  [entity_token: string]: EntityForDisplay;
};

export interface SuppliedDataValue {
  label: string;
  value: string;
  verified?: boolean;
}

interface DocumentType {
  document_type_id: number;
  type: string;
}

interface Props {
  documents: Document[];
  documentTypes: DocumentType[];
  entities: EntitiesMap;
  evaluationToken?: string;
  hideReviewActions?: boolean;
  investigationToken?: string;
  onClose: ModalProps['onClose'];
  onDocumentsUpdate: (args: { document_token: string; entity_token: string }) => void;
  onDocumentUpload?: () => void;
  suppliedDataValues?: SuppliedDataValue[];
  title?: string;
}

// ============================================================
//     COMPONENT
// ============================================================
export const DocumentReviewModalV2: React.FC<Props> = ({
  documents,
  documentTypes: documentTypesProp,
  entities,
  evaluationToken,
  hideReviewActions = false,
  investigationToken,
  onClose,
  onDocumentsUpdate,
  onDocumentUpload,
  suppliedDataValues,
  title = 'Entity Documents',
}) => {
  const { permissions, agent } = useContext(UserContext);
  const addSnackBar = useSnackBar();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const isSurfaceDocsInInvestigationsEnabled = useFeatureFlags('surfaceDocsInInvestigations');
  const isAlloyAdmin = Boolean(agent?.isEmployee);

  const canUpload = isSurfaceDocsInInvestigationsEnabled
    && permissions?.['CREATE:Documents:null']
    && documentTypesProp
    && documentTypesProp.length > 0
    && entities
    && Object.keys(entities).length > 0;

  useEffect(() => {
    datadogRum.addAction('DocReviewV2Modal', { action: 'OPEN_MODAL' });
    return () => {
      datadogRum.addAction('DocReviewV2Modal', { action: 'CLOSE_MODAL' });
    };
  }, []);

  const handleUploadClick = () => {
    if (isAlloyAdmin) {
      addSnackBar({
        title: 'Alloy admins cannot upload documents',
        type: 'danger',
      });
      return;
    }
    setIsUploadModalOpen(true);
  };


  const uploadPopoverWrapper = canUpload
    ? (button: React.ReactNode) => (
      <DocumentUploadPopover
        documentTypes={documentTypesProp}
        entities={entities}
        evaluationToken={evaluationToken}
        investigationToken={investigationToken}
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={() => {
          setIsUploadModalOpen(false);
          onDocumentUpload?.();
        }}
        trigger={button}
        align="start"
        side="bottom"
        title="Upload Document"
      />
    )
    : undefined;

  return (
    <Modal
      isOpen
      autoFocus={false}
      onClose={onClose}
      size="xl"
      header={(
        <Box justifyContent="flex-start" gap={4}>
          <Text size={4} weight={500}>
            {title}
          </Text>
        </Box>
      )}
      contentStyle={{
        maxHeight: 'calc(100vh - 200px)',
        borderBottom: 'none',
        borderTop: 'none',
        paddingTop: 0,
      }}
    >
      <Box direction="column" alignItems="flex-start" justifyContent="flex-start" gap={2} style={{ flexGrow: 1 }}>
        <DocumentReviewer
          documents={documents}
          entities={entities}
          hideReviewActions={hideReviewActions}
          onRenameSuccess={({ entity_token, document_token }) => {
            onDocumentsUpdate({ document_token, entity_token });
          }}
          onReviewSuccess={({ entity_token, document_token }) => {
            onDocumentsUpdate({ entity_token, document_token });
          }}
          {...(canUpload && { onUploadClick: handleUploadClick })}
          uploadPopoverWrapper={uploadPopoverWrapper}
          shouldPinReviewActions
          suppliedDataValues={suppliedDataValues}
        />
      </Box>
    </Modal>
  );
};
